import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslate } from 'react-admin';
import { getSettlementCategories } from '../../services/settlementCategoriesService';
import { apiUploadFile } from '../../services/apiClient';

/**
 * Add Expense / Add Income modal — kalem select + tutar + not + evrak.
 * Expense = bütçe kullanıldı (−); Income = elden alındı / bütçeye girdi (+).
 */
const SettlementEntryModal = ({ open, onClose, entryType, onSubmit }) => {
  const translate = useTranslate();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);
  const fileInputRef = useRef(null);

  const isExpense = entryType === 'expense';
  const title = isExpense
    ? translate('report.add_expense_title', { _: 'Gider Ekle' })
    : translate('report.add_income_title', { _: 'Gelir Ekle' });
  const hint = isExpense
    ? translate('report.add_expense_hint', {
        _: 'Bu tutar ilgili kalemden harcanmış / kullanılmış sayılır (ör. Reklam Bütçesi kullanıldı).',
      })
    : translate('report.add_income_hint', {
        _: 'Bu tutar elden alınmış / bütçeye girmiş sayılır (ör. Reklam Bütçesi alındı).',
      });

  useEffect(() => {
    if (!open) return;
    setAmount('');
    setNote('');
    setCategoryId('');
    setDocuments([]);
    setLoadingCats(true);
    getSettlementCategories(isExpense ? 'expense' : 'income')
      .then((rows) => {
        setCategories(rows);
        if (rows.length === 1) setCategoryId(rows[0].id);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingCats(false));
  }, [open, entryType, isExpense]);

  const selected = categories.find((c) => c.id === categoryId);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const result = await apiUploadFile(file);
        uploaded.push({
          src: result?.src || '',
          title: result?.title || file.name,
          name: file.name,
        });
      }
      setDocuments((prev) => [...prev, ...uploaded.filter((d) => d.src)]);
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const parsed = Number(amount);
    if (!parsed || parsed <= 0 || !categoryId) return;
    setSaving(true);
    try {
      await onSubmit({
        type: isExpense ? 'expense' : 'income',
        amount: parsed,
        note: note.trim(),
        categoryId: selected?.id || categoryId,
        categoryName: selected?.name || '',
        categoryCode: selected?.code || selected?.id || '',
        documents,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || uploading;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 0.5 }}>
          {hint}
        </Typography>

        <FormControl fullWidth margin="dense" required disabled={loadingCats || busy}>
          <InputLabel id="settlement-category-label">
            {translate('report.item_note', { _: 'Kalem' })}
          </InputLabel>
          <Select
            labelId="settlement-category-label"
            label={translate('report.item_note', { _: 'Kalem' })}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {categories.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          autoFocus
          margin="dense"
          label={translate('report.amount', { _: 'Tutar' })}
          type="number"
          fullWidth
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: '0.01' }}
          disabled={busy}
        />
        <TextField
          margin="dense"
          label={translate('report.note', { _: 'Not' })}
          fullWidth
          multiline
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
        />

        <Box sx={{ mt: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
            onChange={handleFilesSelected}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {translate('report.add_document', { _: 'Evrak Ekle' })}
          </Button>
          {documents.length > 0 && (
            <List dense sx={{ mt: 1, bgcolor: '#fafafa', borderRadius: 1 }}>
              {documents.map((doc, index) => (
                <ListItem key={`${doc.src}-${index}`}>
                  <ListItemText
                    primary={doc.title || doc.name || 'Belge'}
                    primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => removeDocument(index)} disabled={busy}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {translate('ra.action.cancel', { _: 'İptal' })}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color={isExpense ? 'error' : 'success'}
          disabled={busy || !(Number(amount) > 0) || !categoryId}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {translate('ra.action.save', { _: 'Kaydet' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettlementEntryModal;
